var _ = require('underscore');
var $ = require('jquery');
var TorsoView = require('backbone-torso/modules/View');
var TorsoEvents = require('backbone-torso/modules/Events');
var wizardButtonsTemplate = require('./wizardButtonsTemplate.hbs');
var wizardPreregExtService = require('./../urls').wizardPreregExtService;
var appointmentExtService = require('./../urls').appointmentExtService;
var launcherAppInterface = require('./../launcherApplicationInterface');
var wizardUtils = require('./wizardUtils');
var applicationUtils = require('./../applicationUtils');
var UserLogEntryModel = require('./../userLog/UserLogEntryModel');
var eregWorkflowConfigService = require('./eregWorkflowConfigService');

var NAVIGATION_DIRECTIONS = wizardUtils.NAVIGATION_DIRECTIONS;

var PAGE_NAMES = {
  PROFILE: 'profile',
  INSURANCE: 'insurance',
  SURVEYS: 'surveys',
  FORMS: 'forms',
  BILL_DETAIL: 'preRegBillDetails',
  PAYMENT: 'payment',
  PAYMENT_DETAIL: 'paymentDetail'
};

var LOG_STATUS_COMPLETE_URL_TEMPLATE = _.template(wizardPreregExtService + '/<%= stepName %>/status');

/**
 * This view renders the wizard buttons. It is meant as a temporary solution for embedding torso pages in the
 * existing VCC wizard. Once all prereg functionality is ported to Torso, wizard should be replaced with a 'Hub and Spoke' model.
 * Don't instantiate this View directly. Use a WizardWidget.
 *
 * Interactions with this view should be managed through WizardWidget (except attaching) and should not be done directly.
 *
 * This handles the specifics of most of the navigation in the wizard and most methods in wizard widget dealing with
 * navigation go through this view.
 *
 * @class WizardButtonsView
 * @constructor
 * @param options {Object} Options to configure this view
 *   @param options.patientVecnaId {String} the vecna id of the patient whose appointment is being displayed
 *   @param options.appointmentExternalId {String} the external id of the appointment being displayed
 *   @param options.regDate {String} the date of the appointments being displayed
 * @extends Torso.View
 */
var WizardButtonsView = TorsoView.extend({
  tagName: 'menu',
  className: 'brec-menu brec-menu--responsive',
  template: wizardButtonsTemplate,

  NAVIGATION_DIRECTIONS: NAVIGATION_DIRECTIONS,
  PAGE_NAMES: PAGE_NAMES,

  events: {
    'click [data-continue-button]': 'continueToNextPage',
    'click [data-back-button]': 'backToPreviousPage',
    'click [data-print-button]': '_triggerPrintEvent',
    'click [data-skip-button]': '_skipToEnd',
    'click [data-payment-button]': 'redirectToContinueUrl',
    'click [data-agree-button]': '_triggerAgreeFormEvent',
    'click [data-decline-button]': '_triggerDeclineFormEvent',
    'click [data-save-and-exit-button]': '_exit'
  },

  /**
   * Sets the current page of the wizard to the page described by the context.
   * @method navigateToWizardPage
   * @param pageContext {Object}
   *   @param pageContext.currentPageName {String} the name of the top-level wizard step.
   *   @param [pageContext.currentChildPageName] {String} the name of the sub-wizard step.
   *   @param [pageContext.questionnaireName] {String} the name of the questionnaire being displayed.
   *   @param [pageContext.formName] {String} the name of the form being displayed.
   *   @param [pageContext.continueAllowed] {Boolean} whether the user can continue on this page.
   */
  navigateToWizardPage: function(pageContext) {
    var currentPageName = pageContext.currentPageName;
    var stepIsEnabled = eregWorkflowConfigService.isEnabledStep({
      topLevelStepName: currentPageName,
      patientVecnaId: this._patientVecnaId,
      appointmentExternalId: pageContext.appointmentExternalId || this._appointmentExternalId
    });

    if (!stepIsEnabled){
      throw new Error('The current page ' + currentPageName + ' is disabled');
    }
    this.set({
      currentPageName: currentPageName,
      currentChildPageName: pageContext.currentChildPageName,
      appointmentExternalId: pageContext.appointmentExternalId,
      questionnaireName: pageContext.questionnaireName,
      formName: pageContext.formName,
      continueAllowed: !_.isBoolean(pageContext.continueAllowed) || pageContext.continueAllowed,
      loadingMessage: pageContext.initialLoadingMessage
    });
  },

  /**
   * Sets the action to take when navigating to the next survey page for the given questionnaire name.
   * @method setNextQuestionnairePageAction
   * @param questionnaireName {String} the name of the questionnaire to register the action for.
   * @param nextAction {Function} A function that returns a promise.  The function is run when the wizard widget
   *                              advances to the next step for the given questionnaire and any wizard specific actions
   *                              (including actual navigation) will only run if the action resolves.
   *                              If the returned promise is rejected the remaining wizard actions are skipped.
   */
  setNextQuestionnairePageAction: function(questionnaireName, nextAction) {
    this._questionnaireNextActions[questionnaireName] = nextAction;
  },

  /**
   * Sets the action to take when navigating to the previous survey page for the given questionnaire name.
   * @method setPreviousQuestionnairePageAction
   * @param questionnaireName {String} the name of the questionnaire to register the action for.
   * @param previousAction {Function} A function that returns a promise.  The function is run when the wizard widget
   *                                  advances to the previous step for the given questionnaire and any wizard specific actions
   *                                  (including actual navigation) will only run if the action resolves.
   *                                  If the returned promise is rejected the remaining wizard actions are skipped.
   */
  setPreviousQuestionnairePageAction: function(questionnaireName, previousAction) {
    this._questionnairePreviousActions[questionnaireName] = previousAction;
  },

  /**
   * Set a loading message to display.  Also disables the primary navigation buttons until this method is called with
   * a falsey argument.
   * @method setLoadingMessage
   * @param [loadingMessage=false] {String|Boolean} If truthy, then it is displayed and the primary buttons are disabled.
   *                                                If falsey, then no message is displayed and the primary buttons are enabled.
   */
  setLoadingMessage: function(loadingMessage) {
    this.set('loadingMessage', loadingMessage);
  },

  /**
   * Shows the print button in the wizard action button.
   * The print button will trigger a 'print' event on this wizardButtonView instance.
   * @method showPrintButton
   */
  showPrintButton: function() {
    this.set('showPrintButton', true);
  },

  /**
   * Hides the print button in the wizard action button.
   * The print button will trigger a 'print' event on this wizardButtonView instance.
   * @method hidePrintButton
   */
  hidePrintButton: function() {
    this.set('showPrintButton', false);
  },

  /**
   * Sends a message to the server saying this step has been completed
   * @method markStepCompleted
   * @public
   */
  markStepCompleted: function() {
    var currentGlobalStepName = this.get('currentPageName');
    var currentSubStepName = this.get('formName') || this.get('questionnaireName') || currentGlobalStepName;
    var logStatusUrl = LOG_STATUS_COMPLETE_URL_TEMPLATE({ stepName: currentSubStepName });
    var combineUpdatePromise = $.Deferred();
    var appointmentExternalId = this.get('appointmentExternalId') || this._appointmentExternalId;
    var subStepAppointmentExternalId = this.get('subStepAppointmentExternalId');
    var appointmentExternalIdsToComplete;
    if (subStepAppointmentExternalId) {
      appointmentExternalIdsToComplete = [ subStepAppointmentExternalId ];
    } else {
      appointmentExternalIdsToComplete = wizardUtils.getPreRegWizardAppointmentExternalIds(appointmentExternalId, this._regDate);
    }
    var wizardStatusUpdateRequest = {
      appointmentExternalIds: appointmentExternalIdsToComplete,
      isOnsite: launcherAppInterface.isAtHospital()
    };
    var statusUpdatePromise = $.ajax({
      contentType: 'application/json',
      type: 'POST',
      dataType: 'json',
      url: logStatusUrl,
      data: JSON.stringify(wizardStatusUpdateRequest)
    });
    if (this.get('isLastQuestionnaire') || this.get('isLastForm')) {
      var logGlobalStepStatusUrl = LOG_STATUS_COMPLETE_URL_TEMPLATE({ stepName: currentGlobalStepName });
      statusUpdatePromise.then(function() {
        return $.ajax({
          contentType: 'application/json',
          type: 'POST',
          dataType: 'json',
          url: logGlobalStepStatusUrl,
          data: JSON.stringify(wizardStatusUpdateRequest)
        })
        .then(function() {
          combineUpdatePromise.resolve();
        }, function() {
          combineUpdatePromise.reject();
        });
      });
    } else {
       statusUpdatePromise.then(function() {
         combineUpdatePromise.resolve();
       }, function() {
         combineUpdatePromise.reject();
       });
    }
    return combineUpdatePromise.promise();
  },

  /**
   * Parses the wizard data retrieved from the server to find the urls for the previous page and next page.
   * @method setWizardData
   * @param wizardData {Object}
   *   @param wizardData.wizardSteps {Object[]} the steps for the root wizard.
   * @param questionnaireWizardData {Object}
   *   @param questionnaireWizardData.wizardSteps {Object[]} the steps for the questionnaire sub wizard.
   * @param formWizardData {Object}
   *   @param questionnaireWizardData.wizardSteps {Object[]} the steps for the form sub wizard.
   */
  setWizardData: function(wizardData, questionnaireWizardData, formWizardData) {
    var wizardId = this._getWizardId();

    var topWizardSteps = wizardData && wizardData.wizardSteps;
    var cleanedWizardSteps = wizardUtils.generateSequentialWizardSteps(topWizardSteps, wizardId);

    var questionnaireWizardSteps = questionnaireWizardData && questionnaireWizardData.wizardSteps;
    var cleanedQuestionnaireWizardSteps = wizardUtils.generateSequentialWizardSteps(questionnaireWizardSteps, wizardId);

    var formWizardSteps = formWizardData && formWizardData.wizardSteps;
    var cleanedFormWizardSteps = wizardUtils.generateSequentialWizardSteps(formWizardSteps, wizardId);
    this.set({
      cleanedWizardSteps: cleanedWizardSteps,
      cleanedQuestionnaireWizardSteps: cleanedQuestionnaireWizardSteps,
      cleanedFormWizardSteps: cleanedFormWizardSteps
    });
  },

  /**
   * @method initialize
   * @param options {Object} Options to configure this view
   *   @param options.patientVecnaId {String} the vecna id of the patient whose appointment is being displayed
   *   @param options.appointmentExternalId {String} the external id of the appointment being displayed
   *   @param options.regDate {String} the date of the appointments being displayed
   *   @param options.userIsStaff {Boolean} true if the user is a staff user (and therefore should use the staff versions of the urls).
   *   @param options.continueAllowed {Boolean} whether the user can continue on this page.
   * @override
   */
  initialize: function(options) {
    _.bindAll(this, 'completeWizardWorkflow', 'markStepCompleted', '_processRegOrCheckinSuccess');
    this._questionnaireNextActions = {};
    this._questionnairePreviousActions = {};

    /**
     * @property _patientVecnaId {String} the vecna id of the patient whose appointment is being displayed
     * @private
     */
    this._patientVecnaId = options.patientVecnaId;

    /**
     * @property _appointmentExternalId {String} the id of the appointment being preregistered
     * @private
     */
    this._appointmentExternalId = options.appointmentExternalId;

    /**
     * @property _regDate {String} the date of the appointments being preregistered
     * @private
     */
    this._regDate = options.regDate;

    /**
     * @property _userIsStaff {String} whether the user is a staff user.
     * @private
     */
    this._userIsStaff = options.userIsStaff;

    this.set('isLastStep', false);
    this.set('isLastQuestionnaire', false);
    this.set('isLastForm', false);

    this.setPaymentAllowed(false);

    if (_.isBoolean(options.continueAllowed)) {
      this.setContinueAllowed(options.continueAllowed);
    } else {
      this.setContinueAllowed(true);
    }

    this.listenTo(this.viewState, 'change:cleanedWizardSteps change:cleanedQuestionnaireWizardSteps change:cleanedFormWizardSteps', this._processWizardData);
    this.listenTo(this.viewState, 'change:currentPageName change:questionnaireName change:formName change:currentChildPageName', this._processWizardData);
    this.listenTo(this.viewState, 'change:appointmentExternalId', this._processWizardData);
    this.listenTo(this.viewState, 'change:currentPageName change:currentChildPageName change:questionnaireName change:formName', this.render);
    this.listenTo(this.viewState, 'change:loadingMessage change:nextPageUrl change:previousPageUrl', this.render);
    this.listenTo(this.viewState, 'change:paymentAllowed change:errors change:showPrintButton change:hideButtons', this.render);
  },

  /**
   * @method prepare
   * @override
   */
  prepare: function() {
    var templateContext = TorsoView.prototype.prepare.apply(this);
    templateContext.patientVecnaId = this._patientVecnaId;
    templateContext.appointmentExternalId = this.get('appointmentExternalId') || this._appointmentExternalId;
    var currentPageName = this.get('currentPageName');
    templateContext.isAppointmentQuestionnairePage = (currentPageName === PAGE_NAMES.SURVEYS);
    templateContext.isAppointmentFormPage = (currentPageName === PAGE_NAMES.FORMS);
    var currentChildPageName = this.get('currentChildPageName');
    templateContext.isAppointmentBillPage = (currentChildPageName === PAGE_NAMES.BILL_DETAIL);
    templateContext.isAppointmentMakePaymentPage = (currentChildPageName === PAGE_NAMES.PAYMENT_DETAIL);
    templateContext.isAtHospital = launcherAppInterface.isAtHospital();
    templateContext.currentApplication = applicationUtils.currentApplication;
    return templateContext;
  },

  /**
   * Generate the identifier for the current wizard (patient id, appt id, reg date).
   * @method _getWizardId
   * @return {{patientVecnaId: *, appointmentExternalId: *, regDate: *}}
   * @private
   */
  _getWizardId: function() {
    return {
      patientVecnaId: this._patientVecnaId,
      appointmentExternalId: this._appointmentExternalId,
      regDate: this._regDate
    };
  },

  /**
   * Set the data from the configuration on the server on the managed views of this service and on this service's properties.
   * @method _processWizardData
   * @private
   */
  _processWizardData: function() {
    var currentRootPageName = this.get('currentPageName');
    var currentPageName = this.get('currentChildPageName') || this.get('currentPageName');
    if (currentRootPageName) {
      var pageCriteria = {
        pageName: currentPageName,
        appointmentExternalId: this.get('appointmentExternalId') || this._appointmentExternalId
      };
      this.setPageAndStepInfo(pageCriteria);
      if (currentRootPageName === PAGE_NAMES.SURVEYS && this.get('questionnaireName')) {
        this._setQuestionnaireUrls();
      } else if (currentRootPageName === PAGE_NAMES.FORMS && this.get('formName')) {
        this._setFormUrls();
      }
    }
  },

  /**
   * Processes the wizard data to determine the next url, previous url and whether this is the last step.
   * @param [options] {Object}
   *   @param [options.pageName] {String} name of current page
   *   @param [options.appointmentExternalId] {String} used to identify which page when a sub-wizard has a page per appt.
   * @method setPageAndStepInfo
   */
  setPageAndStepInfo: function(options) {
    var cleanedWizardSteps = this.get('cleanedWizardSteps');
    if (!cleanedWizardSteps) {
      return;
    }

    var passedInPageName = options && options.pageName;
    var currentPageName = passedInPageName || this.get('currentChildPageName') || this.get('currentPageName');
    var stepName = decodeURIComponent(currentPageName);
    var passedInAppointmentExternalId = options && options.appointmentExternalId;
    var stepCriteria = {
      name: stepName,
      appointmentExternalId: passedInAppointmentExternalId
    };
    var currentStepIndex = this._findWizardStepIndex(cleanedWizardSteps, stepCriteria);
    if (_.isNull(currentStepIndex)) {
      throw new Error('The step ' + currentPageName + ' does not exist or is a top-level wizard step');
    }

    var nextStepIndex = currentStepIndex + 1;
    var currentStepIsNotLast = nextStepIndex < cleanedWizardSteps.length;
    var nextPageUrl = '';
    if (currentStepIsNotLast) {
      var nextStep = cleanedWizardSteps[nextStepIndex];
      nextPageUrl = wizardUtils.getPageUrl({
        hash: nextStep.hash,
        patientVecnaId: this._patientVecnaId,
        appointmentExternalId: this.get('appointmentExternalId') || this._appointmentExternalId,
        regDate: this._regDate,
        stepAppointmentExternalId: nextStep.appointmentExternalId,
        navigationDirection: NAVIGATION_DIRECTIONS.FORWARD
      });
    }

    var previousStepIndex = currentStepIndex - 1;
    var currentStepIsNotFirst = previousStepIndex >= 0;
    var previousPageUrl = '';
    if (currentStepIsNotFirst) {
      var previousStep = cleanedWizardSteps[previousStepIndex];
      previousPageUrl = wizardUtils.getPageUrl({
        hash: previousStep.hash,
        patientVecnaId: this._patientVecnaId,
        appointmentExternalId: this.get('appointmentExternalId') || this._appointmentExternalId,
        regDate: this._regDate,
        stepAppointmentExternalId: previousStep.appointmentExternalId,
        navigationDirection: NAVIGATION_DIRECTIONS.BACK
      });
    }

    var isLastStep = (nextStepIndex === cleanedWizardSteps.length - 1);

    this.set({
      nextPageUrl: nextPageUrl,
      previousPageUrl: previousPageUrl,
      isLastQuestionnaire: false,
      isLastForm: false,
      isLastStep: isLastStep,
      subStepAppointmentExternalId: cleanedWizardSteps[currentStepIndex].appointmentExternalId
    });
  },

  /**
   * Overrides the next and previous page urls with values from the questionnaire sub-steps.
   * @method _setQuestionnaireUrls
   * @private
   */
  _setQuestionnaireUrls: function() {
    var cleanedQuestionnaireWizardSteps = this.get('cleanedQuestionnaireWizardSteps');
    if (!cleanedQuestionnaireWizardSteps) {
      return;
    }
    var questionnaireName = this.get('questionnaireName');
    var appointmentExternalId = this.get('appointmentExternalId');
    var decodedQuestionnaireName = decodeURIComponent(questionnaireName);
    var decodedAppointmentExternalId = decodeURIComponent(appointmentExternalId);
    var currentQuestionnaireStepIndex = this._findQuestionnaireWizardStepIndex(cleanedQuestionnaireWizardSteps, decodedQuestionnaireName, decodedAppointmentExternalId);
    if (_.isNull(currentQuestionnaireStepIndex)) {
      throw new Error('The questionnaire ' + questionnaireName + ' does not exist in the list of questionnaires.');
    }
    var questionnaireStepRootHash = wizardUtils.getPageHash({
      hash: 'appointmentPreRegQuestionnaires',
      patientVecnaId: this._patientVecnaId,
      appointmentExternalId: this.get('appointmentExternalId') || this._appointmentExternalId,
      regDate: this._regDate
    });
    var questionnaireStepRootUrl = applicationUtils.currentApplication.fullUrlPath + '#' + questionnaireStepRootHash;

    var nextQuestionnaireStepIndex = currentQuestionnaireStepIndex + 1;
    var currentQuestionnaireStepIsNotLast = nextQuestionnaireStepIndex < cleanedQuestionnaireWizardSteps.length;
    var nextPageUrl = this.get('nextPageUrl');
    if (currentQuestionnaireStepIsNotLast) {
      var nextQuestionnaireStep = cleanedQuestionnaireWizardSteps[nextQuestionnaireStepIndex];
      var nextQuestionnaireName = nextQuestionnaireStep.urlParams.surveyName;
      var nextAppointmentExternalId = this._regDate ? '/' + nextQuestionnaireStep.appointmentExternalId : '';
      var nextNavigationDirectionQueryParams = wizardUtils.getNavigationDirectionQueryParameters(NAVIGATION_DIRECTIONS.FORWARD);
      nextPageUrl = questionnaireStepRootUrl + nextAppointmentExternalId + '/' + nextQuestionnaireName + nextNavigationDirectionQueryParams;
    }

    var previousStepIndex = currentQuestionnaireStepIndex - 1;
    var currentStepIsNotFirst = previousStepIndex >= 0;
    var previousPageUrl = this.get('previousPageUrl');
    if (currentStepIsNotFirst) {
      var previousStep = cleanedQuestionnaireWizardSteps[previousStepIndex];
      var previousQuestionnaireName = previousStep.urlParams.surveyName;
      var previousAppointmentExternalid = this._regDate ? '/' + previousStep.appointmentExternalId : '';
      var previousNavigationDirectionQueryParams = wizardUtils.getNavigationDirectionQueryParameters(NAVIGATION_DIRECTIONS.BACK);
      previousPageUrl = questionnaireStepRootUrl + previousAppointmentExternalid + '/' + previousQuestionnaireName + previousNavigationDirectionQueryParams;
    }

    var isLastStep = this.get('isLastStep') && (currentQuestionnaireStepIndex === cleanedQuestionnaireWizardSteps.length - 1 );

    this.set({
      nextPageUrl: nextPageUrl,
      previousPageUrl: previousPageUrl,
      isLastQuestionnaire: !currentQuestionnaireStepIsNotLast,
      isLastForm: false,
      isLastStep: isLastStep
    });
  },

  /**
   * Overrides the next and previous page urls with values from the form sub-steps.
   * @method _setFormUrls
   * @private
   */
  _setFormUrls: function() {
    var cleanedFormWizardSteps = this.get('cleanedFormWizardSteps');
    if (!cleanedFormWizardSteps) {
      return;
    }
    var formName = this.get('formName');
    var currentFormStepIndex = this._findFormWizardStepIndex(cleanedFormWizardSteps, formName);
    if (_.isNull(currentFormStepIndex)) {
      throw new Error('The form ' + formName + ' does not exist in the list of forms.');
    }
    var formStepRootHash = wizardUtils.getPageHash({
      hash: 'appointmentPreRegForms',
      patientVecnaId: this._patientVecnaId,
      appointmentExternalId:  this.get('appointmentExternalId') || this._appointmentExternalId,
      regDate: this._regDate
    });
    var formStepRootUrl = applicationUtils.currentApplication.fullUrlPath + '#' + formStepRootHash;

    var nextFormStepIndex = currentFormStepIndex + 1;
    var currentFormStepIsNotLast = nextFormStepIndex < cleanedFormWizardSteps.length;
    var nextPageUrl = this.get('nextPageUrl');
    if (currentFormStepIsNotLast) {
      var nextFormStep = cleanedFormWizardSteps[nextFormStepIndex];
      var nextFormName = nextFormStep.urlParams.name;
      var nextNavigationDirectionQueryParams = wizardUtils.getNavigationDirectionQueryParameters(NAVIGATION_DIRECTIONS.FORWARD);
      nextPageUrl = formStepRootUrl + '/' + nextFormName + nextNavigationDirectionQueryParams;
    }

    var previousStepIndex = currentFormStepIndex - 1;
    var currentStepIsNotFirst = previousStepIndex >= 0;
    var previousPageUrl = this.get('previousPageUrl');
    if (currentStepIsNotFirst) {
      var previousStep = cleanedFormWizardSteps[previousStepIndex];
      var previousFormName = previousStep.urlParams.name;
      var previousNavigationDirectionQueryParams = wizardUtils.getNavigationDirectionQueryParameters(NAVIGATION_DIRECTIONS.BACK);
      previousPageUrl = formStepRootUrl + '/' + previousFormName + previousNavigationDirectionQueryParams;
    }

    var isLastStep = this.get('isLastStep') && (currentFormStepIndex === cleanedFormWizardSteps.length - 1 );

    this.set({
      nextPageUrl: nextPageUrl,
      previousPageUrl: previousPageUrl,
      isLastQuestionnaire: false,
      isLastForm: !currentFormStepIsNotLast,
      isLastStep: isLastStep
    });
  },

  /**
   * Triggers a 'print' event on this view.
   * @method _triggerPrintEvent
   * @param event {jQuery.Event} that triggered the print event.
   * @private
   */
  _triggerPrintEvent: function(event) {
    event.preventDefault();
    this.trigger('print');
  },

  /**
   * Triggers a 'sign-form' event with value true on this view.
   * @method _triggerAgreeFormEvent
   * @param event {jQuery.Event} that triggered the sign-form event.
   * @private
   */
  _triggerAgreeFormEvent: function(event) {
    event.preventDefault();
    this._triggerSignFormEvent(true);
  },

  /**
   * Triggers a 'sign-form' event with value false on this view.
   * @method _triggerDeclineFormEvent
   * @param event {jQuery.Event} that triggered the sign-form event.
   * @private
   */
  _triggerDeclineFormEvent: function(event) {
    event.preventDefault();
    this._triggerSignFormEvent(false);
  },

  /**
   * Triggers a 'sign-form' event with given value on this view.
   * @method _triggerSignFormEvent
   * @param agreed {Boolean} the boolean passed to the triggered event.
   * @private
   */
  _triggerSignFormEvent: function(agreed) {
    this.trigger('sign-form', agreed);
  },

  /**
   * Redirects to url set on the element via the data-continue-url attribute
   * @param event {jQuery.Event} the click event that triggered this call.
   */
  redirectToContinueUrl: function(event) {
    if (event) {
      event.preventDefault();
      var $target = $(event.currentTarget);
      var targetUrl = $target.attr('data-continue-url');
      wizardUtils.navigateToLink(targetUrl);
    }
  },

  /**
   * Redirects to url set on the element via the data-exit-url attribute
   * @param event {jQuery.Event} the click event that triggered this call.
   */
  _exit: function(event) {
    if (event) {
      event.preventDefault();
      var $target = $(event.currentTarget);
      var exitUrl = $target.attr('data-exit-url');
      wizardUtils.navigateToLink(exitUrl);
    }
  },

  /**
   * Marks this step as completed and then advances to the next step in the wizard
   * @method continueToNextPage
   * @param [event] {jQuery.Event} the click event that triggered this call.
   * @param [forceWizardCompletion] {Boolean} true to force wizard to complete
   */
  continueToNextPage: function(event, forceWizardCompletion) {
    var self = this;
    var isWizardDone = this.get('isLastStep') || forceWizardCompletion;
    if (event) {
      event.preventDefault();
    }

    if (this.get('continueAllowed')) {
      this.set('errors', {
        continue: false
      });

      this.setLoadingMessage($.i18n.prop('wizard.buttons.loading'));
      this.trigger('change:navigationDirection', NAVIGATION_DIRECTIONS.FORWARD);
      var nextActionPromise = $.Deferred().resolve().promise();
      var nextQuestionnaireAction = this._questionnaireNextActions[this.get('questionnaireName')];
      if (_.isFunction(nextQuestionnaireAction)) {
        nextActionPromise = nextQuestionnaireAction();
      }
      nextActionPromise.then(function() {
          TorsoEvents.trigger('wizard:continue');
        })
        .then(this.markStepCompleted)
        .then(function() {
          if (isWizardDone) {
            return self.completeWizardWorkflow();
          }
        })
        .then(function(workflowCompletedUrl) {
          /**
           * Redirect to final details page if completed
           * workflowCompletedUrl only exist when completedWizardWorkflow is called
           */
          if (workflowCompletedUrl) {
            wizardUtils.navigateToLink(workflowCompletedUrl);
          } else {
            var nextPageUrl = self.get('nextPageUrl');
            if (event && event.currentTarget) {
              var $target = $(event.currentTarget);
              var targetUrl = $target.attr('data-continue-url');
              if (targetUrl) {
                nextPageUrl = targetUrl;
              }
            }
            wizardUtils.navigateToLink(nextPageUrl);
          }
        });
    } else {
      this.set('errors', {
        continue: true
      });
    }
  },

  /**
   * Marks navigates back a page if the _previousAction() returns true.
   * @method backToPreviousPage
   * @param [event] {jQuery.Event} the click event that triggered this call.
   */
  backToPreviousPage: function(event) {
    if (event) {
      event.preventDefault();
    }
    this.setLoadingMessage($.i18n.prop('wizard.buttons.loading'));
    this.trigger('change:navigationDirection', NAVIGATION_DIRECTIONS.BACK);
    var previousActionPromise = $.Deferred().resolve().promise();
    var previousQuestionnaireAction = this._questionnairePreviousActions[this.get('questionnaireName')];
    if (_.isFunction(previousQuestionnaireAction)) {
      previousActionPromise = previousQuestionnaireAction();
    }
    previousActionPromise.then(function() {
      TorsoEvents.trigger('wizard:back');
      wizardUtils.navigateToLink(this.get('previousPageUrl'));
    }.bind(this));
  },

  /**
   * Skips to the last page of the wizard
   * Marks wizard as completed
   *
   * @method _skipToEnd
   * @param event {jQuery.Event} click event on a link
   * @private
   */
  _skipToEnd: function(event) {
    this.continueToNextPage(event, true);
  },

  /**
   * Sets workflow-related flags and sends a message to the server saying
   * the appointment has been preregistered/checked in.
   * @method completeWizardWorkflow
   * @returns {String} Redirect URL
   */
  completeWizardWorkflow: function() {
    var isCheckin = launcherAppInterface.isAtHospital();
    var appointmentAction;
    var regCompletedLogEntry = new UserLogEntryModel();
    var workObjects = [];
    var appointmentExternalIds = wizardUtils.getSelectedPreRegWizardAppointmentExternalIds(this._appointmentExternalId, this._regDate);
    _.each(appointmentExternalIds, function(appointmentExternalId) {
      workObjects.push({ workObjectType: 'APPOINTMENT', workObjectId: appointmentExternalId });
    });
    regCompletedLogEntry.set('workObjects', workObjects);

    if (isCheckin) {
      appointmentAction = 'checkin';
      regCompletedLogEntry.set('action', 'END_EREG');
    } else {
      appointmentAction = 'preregister';
      regCompletedLogEntry.set('action', 'END_PREREG');
    }
    regCompletedLogEntry.save();
    var checkinOrRegisterAppointmentUrl = appointmentExtService + '/' + appointmentAction;
    return $.ajax({
      contentType: 'application/json',
      type: 'POST',
      dataType: 'json',
      url: checkinOrRegisterAppointmentUrl,
      data: JSON.stringify({ appointmentExternalIds: appointmentExternalIds })
    })
    .then(this._processRegOrCheckinSuccess);
  },

  /**
   * Check-in or registration was successful.  Process the response.
   * @param response {Object} the response from the server to process.
   *   @param response.list {Object[]} the resulting appointments that were processed by this request.
   * @return {String} the url to redirect to based on this response.
   * @private
   */
  _processRegOrCheckinSuccess: function(response) {
    if (_.isUndefined(response) || _.isUndefined(response.list)) {
      return applicationUtils.currentApplication.fullUrlPath;
    }
    var rootHash = wizardUtils.getRegBaseHash({
      patientVecnaId: this._patientVecnaId,
      appointmentExternalId: this._appointmentExternalId,
      regDate: this._regDate
    });
    var url = applicationUtils.currentApplication.fullUrlPath + '#' + rootHash;
    var someAppointmentsNotCheckedIn = _.some(response.list, function(appointmentDTO) {
      return !appointmentDTO.externalAppointment.checkedIn;
    });
    var isCheckin = launcherAppInterface.isAtHospital();
    if (isCheckin && someAppointmentsNotCheckedIn) {
      url += '/queued';
    }
    return url;
  },

  /**
   * @method setPaymentAllowed set viewState paymentAllowed, determine whether to show the make payment button or not
   * @param paymentAllowed {Boolean} if true, show the make payment button, otherwise hide the make payment button
   */
  setPaymentAllowed: function(paymentAllowed) {
    this.set('paymentAllowed', paymentAllowed);
  },

  /**
   * Set viewState continueAllowed, determine whether to allow user to continue to next step or not
   * @method setContinueAllowed
   * @param continueAllowed {Boolean} if true, allow user to navigate to next step, otherwise show error message
   */
  setContinueAllowed: function (continueAllowed) {
    this.set('continueAllowed', continueAllowed);
  },

  /**
   * Cleans wizard steps for easier processing
   * First, removes disabled top-level steps, then replaces wizard steps by their child wizard steps
   *
   * @method _cleanWizardSteps
   * @param wizardSteps {Array} to clean
   * @return {Array} cleaned wizard
   * @private
   */
  _cleanWizardSteps: function(wizardSteps) {
    var sequentialWizardSteps = this.get('sequentialWizardSteps');

    if (!_.isEmpty(sequentialWizardSteps)) {
      return sequentialWizardSteps;
    }

    var generatedSequentialWizardSteps = wizardUtils.generateSequentialWizardSteps(wizardSteps);
    this.set('sequentialWizardSteps', generatedSequentialWizardSteps);
    return generatedSequentialWizardSteps;
  },

  /**
   * Finds the index of the wizard step with a particular pageName
   *
   * @method _findWizardStepIndex
   * @param cleanedWizard {Array} an array of wizard steps with no child wizards
   * @param stepCriteria {Object}
   *   @param stepCriteria.name {String} of step to find
   *   @param stepCriteria.appointmentExternalId {String} the appt ext id to use if the step name isn't sufficient.
   * @return {Number} index of page in wizard steps, null if page not found
   * @private
   */
  _findWizardStepIndex: function(cleanedWizard, stepCriteria) {
    return _.reduce(cleanedWizard, function(previousIndex, wizardStep, currentIndex) {
      var currentPageIsThisStep = wizardStep.name === stepCriteria.name;
      var matchesAppointmentExternalId = !wizardStep.appointmentExternalId || wizardStep.appointmentExternalId === stepCriteria.appointmentExternalId;
      if (!previousIndex && currentPageIsThisStep && matchesAppointmentExternalId) {
        return currentIndex;
      } else {
        return previousIndex;
      }
    }, null);
  },

  /**
   * Finds the index of the wizard step with a particular surveyName
   *
   * @method _findQuestionnaireWizardStepIndex
   * @param cleanedQuestionnaireWizard {Array} an array of questionnaire wizard steps with no child wizards
   * @param questionnaireName {String} of the questionnaire to find
   * @param appointmentExternalId {String} externalId of the appointment
   * @return {Number} index of page in wizard steps, null if page not found
   * @private
   */
  _findQuestionnaireWizardStepIndex: function(cleanedQuestionnaireWizard, questionnaireName, appointmentExternalId) {
    return _.reduce(cleanedQuestionnaireWizard, function(previousIndex, questionnaireWizardStep, currentIndex) {
      var questionnaireMatches = questionnaireWizardStep.urlParams.surveyName === encodeURIComponent(questionnaireName);
      var appointmentMatches = questionnaireWizardStep.appointmentExternalId === encodeURIComponent(appointmentExternalId);
      var currentPageIsThisStep = questionnaireMatches && appointmentMatches;
      if (currentPageIsThisStep) {
        return currentIndex;
      } else {
        return previousIndex;
      }
    }, null);
  },

 /**
   * Finds the index of the wizard step with a particular name
   *
   * @method _findFormWizardStepIndex
   * @param cleanedFormWizard {Array} an array of form wizard steps with no child wizards
   * @param formName {String} of the form to find
   * @return {Number} index of page in wizard steps, null if page not found
   * @private
   */
  _findFormWizardStepIndex: function(cleanedFormWizard, formName) {
    return _.reduce(cleanedFormWizard, function(previousIndex, formWizardStep, currentIndex) {
      var currentPageIsThisStep = formWizardStep.urlParams.name === encodeURIComponent(formName);
      if (currentPageIsThisStep) {
        return currentIndex;
      } else {
        return previousIndex;
      }
    }, null);
  }
});

WizardButtonsView.NAVIGATION_DIRECTIONS = NAVIGATION_DIRECTIONS;
WizardButtonsView.PAGE_NAMES = PAGE_NAMES;

module.exports = WizardButtonsView;
